import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import WavingIcon from '@mui/icons-material/EmojiPeople';
import LanguageIcon from '@mui/icons-material/Language';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { observer } from 'mobx-react';
import React from 'react';

import { RouterLinkDiv } from 'src/components/general/routerLink';
import {
  DISPLAY_PHARMACY_RECOMMENDATIONS,
  HIDE_INQUIRIES,
  POP_IN_V2,
  RESTRICT_QUEUE_PAGE_ACCESS,
} from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { PharmacySvgIcon } from 'src/pharmacyRecommendations/PharmacyIcon';
import type { Provider, Provider as ProviderInstance } from 'src/shared/stores/resource';
import type { RootStore } from 'src/stores/root';
import { ProviderRole } from 'src/stores/users/userType';
import { inject } from 'src/util/inject';
import { patientsQueryParamsForUser } from 'src/util/nav';

const EXTERNAL_LINKS = [
  {
    label: 'Help Desk',
    url: 'https://helpdesk.boulder.care',
  },
  {
    label: 'Provider Resources',
    url: 'https://www.notion.so/boulder/Provider-Team-ed1b62a0f91a480189a2f6b89d091b96',
  },
];

const AdminSidebar = ({ rootStore: { auth } }: { rootStore: RootStore }) => {
  const hideInquiries = useFeatureFlag(HIDE_INQUIRIES);
  const restrictQueuePageAccess = useFeatureFlag(RESTRICT_QUEUE_PAGE_ACCESS);
  const displayPharmacyRecommendations = useFeatureFlag(DISPLAY_PHARMACY_RECOMMENDATIONS);
  const isV2Enabled = useFeatureFlag(POP_IN_V2);
  const isClinician = (auth.user as Provider)?.teamRole === ProviderRole.Clinician;
  const showQueuePage = !(restrictQueuePageAccess && isClinician);
  return (
    <div>
      <List>
        <ListItemButton
          component={RouterLinkDiv}
          routeName="patients"
          queryParams={patientsQueryParamsForUser(auth.user as ProviderInstance)}
        >
          <ListItemIcon>
            <PeopleIcon />
          </ListItemIcon>
          <ListItemText primary="Patients" />
        </ListItemButton>
        <ListItemButton component={RouterLinkDiv} routeName="providers">
          <ListItemIcon>
            <PersonIcon />
          </ListItemIcon>
          <ListItemText primary="Providers" />
        </ListItemButton>
        <ListItemButton
          component={RouterLinkDiv}
          routeName="showProvider"
          params={{ id: auth.user?.id || '' }}
        >
          <ListItemIcon>
            <CalendarIcon />
          </ListItemIcon>
          <ListItemText primary="My Calendar" />
        </ListItemButton>
        {showQueuePage && (
          <ListItemButton
            component={RouterLinkDiv}
            routeName={isV2Enabled ? 'popInClinicQueue' : 'dropInClinicRequests'}
          >
            <ListItemIcon>
              <WavingIcon />
            </ListItemIcon>
            <ListItemText primary="Pop-In Queue" />
          </ListItemButton>
        )}
        {!hideInquiries && (
          <ListItemButton component={RouterLinkDiv} routeName="inquiries">
            <ListItemIcon>
              <AssignmentIndIcon />
            </ListItemIcon>
            <ListItemText primary="Inquiries" />
          </ListItemButton>
        )}
        <ListItemButton component={RouterLinkDiv} routeName="erx">
          <ListItemIcon>
            <LocalPharmacyIcon />
          </ListItemIcon>
          <ListItemText primary="DoseSpot" />
        </ListItemButton>
        {displayPharmacyRecommendations && (
          <ListItemButton component={RouterLinkDiv} routeName="pharmacyRecommendations">
            <ListItemIcon>
              <PharmacySvgIcon />
            </ListItemIcon>
            <ListItemText primary="Pharmacy Recommendations" />
          </ListItemButton>
        )}
        <Divider />
        {EXTERNAL_LINKS.map(link => (
          <ListItemButton key={link.url} onClick={() => window.open(link.url, '_blank')}>
            <ListItemIcon>
              <LanguageIcon />
            </ListItemIcon>
            <ListItemText primary={link.label} />
          </ListItemButton>
        ))}
      </List>
    </div>
  );
};

export default inject('rootStore')(observer(AdminSidebar));
